/**
 * Utilidades de logging que solo funcionan en ambientes de desarrollo
 * 
 * @description Este módulo proporciona funciones de logging que automáticamente
 * verifican si estamos en producción y solo muestran logs en desarrollo.
 * 
 * @example
 * import { devLog, logDev, logWithPrefix } from '../utils/logger';
 * 
 * devLog.log('Mi mensaje');
 * logDev('Información de debug');
 * logWithPrefix('API', 'Usuario autenticado');
 */

/** 
 * Constante que determina si estamos en un ambiente de desarrollo
 * Centraliza la comparación para evitar repetición de código
 */
const IS_DEVELOPMENT = process.env.NODE_ENV !== 'production';

/**
 * Objeto que encapsula todos los métodos de console para desarrollo
 * Solo ejecuta los logs cuando NODE_ENV !== 'production'
 */
export const devLog = {
    /**
     * Equivalente a console.log pero solo en desarrollo
     * @param args - Argumentos a pasar a console.log
     */
    log: (...args: any[]) => {
        if (IS_DEVELOPMENT) {
            console.log(...args);
        }
    },

    /**
     * Equivalente a console.warn pero solo en desarrollo
     * @param args - Argumentos a pasar a console.warn
     */
    warn: (...args: any[]) => {
        if (IS_DEVELOPMENT) {
            console.warn(...args);
        }
    },

    /**
     * Equivalente a console.error pero solo en desarrollo
     * @param args - Argumentos a pasar a console.error
     */
    error: (...args: any[]) => {
        if (IS_DEVELOPMENT) {
            console.error(...args);
        }
    },

    /**
     * Equivalente a console.info pero solo en desarrollo
     * @param args - Argumentos a pasar a console.info
     */
    info: (...args: any[]) => {
        if (IS_DEVELOPMENT) {
            console.info(...args);
        }
    },

    /**
     * Equivalente a console.debug pero solo en desarrollo
     * @param args - Argumentos a pasar a console.debug
     */
    debug: (...args: any[]) => {
        if (IS_DEVELOPMENT) {
            console.debug(...args);
        }
    },

    /**
     * Equivalente a console.table pero solo en desarrollo
     * @param data - Datos a mostrar en tabla
     * @param columns - Columnas específicas a mostrar (opcional)
     */
    table: (data: any, columns?: string[]) => {
        if (IS_DEVELOPMENT) {
            console.table(data, columns);
        }
    },

    /**
     * Equivalente a console.group pero solo en desarrollo
     * @param label - Etiqueta del grupo (opcional)
     */
    group: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.group(label);
        }
    },

    /**
     * Equivalente a console.groupCollapsed pero solo en desarrollo
     * @param label - Etiqueta del grupo (opcional)
     */
    groupCollapsed: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.groupCollapsed(label);
        }
    },

    /**
     * Equivalente a console.groupEnd pero solo en desarrollo
     */
    groupEnd: () => {
        if (IS_DEVELOPMENT) {
            console.groupEnd();
        }
    },

    /**
     * Equivalente a console.time pero solo en desarrollo
     * @param label - Etiqueta del timer (opcional)
     */
    time: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.time(label);
        }
    },

    /**
     * Equivalente a console.timeEnd pero solo en desarrollo
     * @param label - Etiqueta del timer (opcional)
     */
    timeEnd: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.timeEnd(label);
        }
    },

    /**
     * Equivalente a console.count pero solo en desarrollo
     * @param label - Etiqueta del contador (opcional)
     */
    count: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.count(label);
        }
    },

    /**
     * Equivalente a console.countReset pero solo en desarrollo
     * @param label - Etiqueta del contador (opcional)
     */
    countReset: (label?: string) => {
        if (IS_DEVELOPMENT) {
            console.countReset(label);
        }
    }
};

/**
 * Función de utilidad simple para logging básico con prefijo [DEV]
 * @param message - Mensaje a mostrar
 * @param data - Datos adicionales opcionales
 * 
 * @example
 * logDev('Usuario autenticado');
 * logDev('Datos recibidos', { id: 123, name: 'Juan' });
 */
export const logDev = (message: string, ...data: any[]) => {
    if (IS_DEVELOPMENT) {
        if (data.length > 0) {
            console.log(`[DEV] ${message}`, ...data);
        } else {
            console.log(`[DEV] ${message}`);
        }
    }
};

/**
 * Función para logging con prefijo personalizado
 * @param prefix - Prefijo a agregar al mensaje
 * @param message - Mensaje a mostrar
 * @param data - Datos adicionales opcionales
 * 
 * @example
 * logWithPrefix('API', 'Respuesta recibida');
 * logWithPrefix('VALIDATION', 'Error en campo', { field: 'email' });
 */
export const logWithPrefix = (prefix: string, message: string, ...data: any[]) => {
    if (IS_DEVELOPMENT) {
        if (data.length > 0) {
            console.log(`[${prefix}] ${message}`, ...data);
        } else {
            console.log(`[${prefix}] ${message}`);
        }
    }
};

/**
 * Función para logging con diferentes niveles de severidad
 * @param level - Nivel de logging ('info', 'warn', 'error', 'debug')
 * @param prefix - Prefijo personalizado
 * @param message - Mensaje a mostrar
 * @param data - Datos adicionales opcionales
 * 
 * @example
 * logLevel('error', 'AUTH', 'Token inválido');
 * logLevel('info', 'USER', 'Sesión iniciada', { userId: 123 });
 */
export const logLevel = (
    level: 'info' | 'warn' | 'error' | 'debug' | 'log',
    prefix: string,
    message: string,
    ...data: any[]
) => {
    if (IS_DEVELOPMENT) {
        const logMethod = console[level] || console.log;
        if (data.length > 0) {
            logMethod(`[${prefix}] ${message}`, ...data);
        } else {
            logMethod(`[${prefix}] ${message}`);
        }
    }
};

/**
 * Utilidad para crear loggers con prefijo fijo
 * @param prefix - Prefijo que se usará en todos los logs
 * @returns Objeto con métodos de logging que usan el prefijo fijo
 * 
 * @example
 * const apiLogger = createLogger('API');
 * apiLogger.info('Conectado al servidor');
 * apiLogger.error('Error de conexión');
 */
export const createLogger = (prefix: string) => ({
    log: (message: string, ...data: any[]) => logWithPrefix(prefix, message, ...data),
    info: (message: string, ...data: any[]) => logLevel('info', prefix, message, ...data),
    warn: (message: string, ...data: any[]) => logLevel('warn', prefix, message, ...data),
    error: (message: string, ...data: any[]) => logLevel('error', prefix, message, ...data),
    debug: (message: string, ...data: any[]) => logLevel('debug', prefix, message, ...data),
});

/**
 * Función para logging condicional basado en una condición
 * @param condition - Condición que debe cumplirse para mostrar el log
 * @param message - Mensaje a mostrar
 * @param data - Datos adicionales opcionales
 * 
 * @example
 * logIf(user.isAdmin, 'Acceso de administrador detectado');
 * logIf(response.status !== 200, 'Respuesta inesperada', response);
 */
export const logIf = (condition: boolean, message: string, ...data: any[]) => {
    if (IS_DEVELOPMENT && condition) {
        if (data.length > 0) {
            console.log(`[CONDITIONAL] ${message}`, ...data);
        } else {
            console.log(`[CONDITIONAL] ${message}`);
        }
    }
};

// Re-exportación por defecto del objeto devLog para conveniencia
export default devLog; 